import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { useHubContext } from '@atlassian/company-hub-common';
import { HubBreadcrumbs } from '@atlassian/company-hub-common/hub-breadcrumbs';
import { Navbar } from '@atlassian/hub-app-shell';

interface HubShellContentComponentProps {
	editorView: EditorView;
}

/**
 * Renders the hub app shell Navbar and breadcrumbs as visual chrome inside the
 * editor content area. DOM portals are used to inject the elements directly
 * before and after the ProseMirror editable div so they occupy the same flex
 * column as the editable content. As a result:
 *
 *  - The Navbar and breadcrumbs appear above the ADF content (below the Atlaskit toolbar).
 *  - When the editor config panel opens on the right, they shrink together with the ADF content.
 *
 * React portals preserve the full React context tree (IntlProvider,
 * HubContext, NavigationStyleContext, etc.) even though the DOM nodes are
 * injected elsewhere.
 */
export const HubShellContentComponent = ({
	editorView,
}: HubShellContentComponentProps): React.ReactNode => {
	const navbarContainerRef = useRef<HTMLDivElement | null>(null);
	const [mounted, setMounted] = useState(false);
	const { isInPreviewMode } = useHubContext();

	useEffect(() => {
		const pmDom = editorView?.dom;
		if (!pmDom) {
			return;
		}

		const pmParent = pmDom.parentElement;
		if (!pmParent) {
			return;
		}

		// Insert the Navbar wrapper BEFORE the ProseMirror editable div.
		//
		// To make the navbar span the editor's full width (matching how
		// `full-width` layout extensions break out of the line-length
		// constraint), we mirror the structure used by editor breakout marks
		// (see `BreakoutView` in `@atlaskit/editor-plugin-breakout`):
		//
		//   - The outer element is a flex container that centers its child.
		//     `transform: none` neutralises any inherited transforms that
		//     would otherwise interfere with the inner element overflowing
		//     past the line-length-constrained parent.
		//   - The inner element gets a `min-width` matching the breakout
		//     formula. It uses the same CSS variables the editor exposes on
		//     its content container, so the navbar automatically scales with
		//     the editor's full-width layout, the viewport, and the
		//     guttering/context-panel state.
		const navbarOuterEl = document.createElement('div');
		navbarOuterEl.setAttribute('data-hub-shell-navbar', 'true');
		navbarOuterEl.style.display = 'flex';
		navbarOuterEl.style.justifyContent = 'center';
		navbarOuterEl.style.transform = 'none';

		const navbarInnerEl = document.createElement('div');

		// This is the same width as set for hub editor
		navbarInnerEl.style.minWidth =
			'min(calc(100cqw - var(--hub-editor-gutter-padding-inline) * 2 - 8px), var(--ak-editor--full-width-layout-width))';

		navbarOuterEl.appendChild(navbarInnerEl);
		pmParent.insertBefore(navbarOuterEl, pmDom);
		navbarContainerRef.current = navbarInnerEl;

		setMounted(true);

		return () => {
			navbarOuterEl.remove();
			navbarContainerRef.current = null;
		};
	}, [editorView]);

	if (!mounted || !navbarContainerRef.current) {
		return null;
	}

	return (
		<>
			{createPortal(
				// In preview mode the navbar should be fully interactive so authors
				// can test navigation. In edit mode pointer-events are disabled to
				// prevent accidental navigation while editing.
				<>
					<Navbar isEditMode={!isInPreviewMode} />
					<HubBreadcrumbs />
				</>,
				navbarContainerRef.current,
			)}
		</>
	);
};
